import classnames from 'classnames';
import { useEffect, useMemo, useState } from 'react';

export type IconSizes = 'x-small' | 'small' | 'medium' | 'large' | 'x-large' | 'xx-large' | 'jumbo';

export interface IconProps {
  name: string;
  size?: IconSizes;
  className?: string;
  url?: string;
}

// Cache to store loaded icons
const iconCache: Record<string, React.ElementType> = {};

const Icon = ({ name, size = 'large', className, url }: IconProps) => {
  const [SvgIcon, setSvgIcon] = useState<React.ElementType | null>(iconCache[name] || null);

  // Memoize className string
  const iconClasses = useMemo(
    () =>
      classnames(
        {
          'size-12': size === 'jumbo',
          'size-10': size === 'xx-large',
          'size-8': size === 'x-large',
          'size-6': size === 'large',
          'size-5': size === 'medium',
          'size-4 min-w-4': size === 'small',
          'size-3': size === 'x-small'
        },
        className
      ),
    [size, className]
  );

  // Only load if not in cache
  useEffect(() => {
    if (iconCache[name]) {
      setSvgIcon(iconCache[name]);
      return;
    }

    let isMounted = true;

    async function loadIcon() {
      try {
        // Dynamically import the SVG as a React component
        const icon = await import(`../../assets/icons/${name}.svg?react`);
        if (icon) {
          const importedIcon = () => icon.default;
          iconCache[name] = importedIcon;
          if (isMounted) {
            setSvgIcon(importedIcon);
          }
        }
      } catch (error) {
        console.error(`No icon found for name: ${name}`, error);
        setSvgIcon(null);
      }
    }

    if (!url) loadIcon();

    return () => {
      isMounted = false;
    };
  }, [name, url]);

  if (url) {
    return <img src={url} className={iconClasses} alt={name} />;
  }

  if (!SvgIcon) {
    // Optionally, return a default icon or null if the specified icon cannot be found
    return <span className={classnames('inline-block', iconClasses)} />;
  }

  return <SvgIcon className={iconClasses} role="presentation" />;
};

export default Icon;
